import { useLinked } from '@linked/react/lib';
import { Chip, IconButton, styled } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { Collection, Events, Linked, Store } from '@type-r/models';
import { asCurrency, asDate, asTimelessDate, CollapsableSection, Column, Columns, Flex, FormInput, InlineTable, Input, Link, Loading, PickItem, PopupDialog, PopupIODialog, PrimaryButton, TableCell, TableHeader, TableRow, TableTotals, toLinkedBoolean } from 'lqx-react';
import * as React from 'react';
import { fetchJson } from 'api/endpoint';
import { InvoiceAssetData, InvoiceInfo, RemittanceAdvice } from 'src/app/pages/inmatch/api';
import { UnresolvedRemittanceAdvice } from '../../api/unresolvedRemittanceAdvice';
import { Alert } from '@material-ui/lab';
import SearchIcon from '@material-ui/icons/SearchOutlined';
import { Button } from 'lqx-react/src/forms/buttons';
import { InblockIcon } from 'common/invoice-aging/iconography';
import { inblockIconStyles } from 'common/invoice-aging';
import WarningIcon from '@material-ui/icons/Warning';
import { CompanyModel, Supplier } from 'api/relationship';
import { useEffect } from 'react';

interface FailedParsing
{
    data: object[];
    errorIndexes: number[];
}

type FailedParsingType = FailedParsing | true;

export const InvoicesList = ({ remittanceAdvice, loading, noEdits } : {
    remittanceAdvice : RemittanceAdvice | UnresolvedRemittanceAdvice
    loading? : boolean
    noEdits? : boolean
}) => {
    const $parsingFailedDialogData = useLinked<FailedParsingType>(null);
    const $openInvoiceLookup = useLinked<InvoiceInfo>(null);
    const missingSupplier = remittanceAdvice.isMissingSupplierName;

    return (
    <CollapsableSection title="Invoices">
        { !loading && remittanceAdvice.customerPlatformCompanyId && remittanceAdvice.supplierPlatformCompanyId ? (
            <>
            { !remittanceAdvice.invoices.length && <Alert severity="error">No available invoices for selected companies</Alert> }

            <Input placeholder="Paste invoices table..." onPaste={ (event: React.ClipboardEvent<HTMLInputElement>) => {
                event.preventDefault();

                const cellRows = getRowsFromPaste(event);
                const rows = getTextFromRows(cellRows);

                if (!rows || !rows.length || !rows.every(r => r.length === rows[0].length)) {
                    $parsingFailedDialogData.true();

                    return;
                }

                const firstRow = rows[0];

                let includeFirstRow = false;
                for (const cell of firstRow) {
                    let countNumbers = 0;

                    for (let i = 0; i < cell.length; i++) {
                        const c = cell.charAt(i);
                        if (c >= '0' && c <= '9') {
                            countNumbers++;
                        }
                    }

                    // if numbers are more than 20% of the cell assume its not a title
                    if (!cell.length || countNumbers / cell.length > 0.2) {
                        includeFirstRow = true;
                        break;
                    }
                }

                const data = [];

                for (let i = includeFirstRow ? 0 : 1; i < rows.length; i++) {
                    const dataRow = {};

                    for (let j = 0; j < rows[i].length; j++) {
                        dataRow[includeFirstRow ? j : firstRow[j]] = rows[i][j];
                    }

                    data.push(dataRow);
                }

                const dto = {
                    customerId: remittanceAdvice.customerPlatformCompanyId,
                    supplierId: remittanceAdvice.supplierPlatformCompanyId,
                    data
                };

                fetchJson('POST', '/api/v2/recs/remittance-advice/parse-invoices', dto)
                    .then((result: InvoiceInfo[]) => {
                        let matchAdded = false;

                        const correctInvoices: InvoiceInfo[] = [];
                        const errorIndexes: number[] = [];

                        for (let i=0; i < result.length; i++) {
                            if (result[i].invoiceNumber) {
                                correctInvoices.push(result[i]);
                            }
                            else {
                                errorIndexes.push(i);
                            }
                        }

                        for (const receivedInvoice of correctInvoices) {
                            const invoice = remittanceAdvice.invoices.find(i => i.invoiceNumber === receivedInvoice.invoiceNumber && i.currency === remittanceAdvice.currency);

                            if (!remittanceAdvice.matches.includes(invoice)) {
                                const match = addMatch(remittanceAdvice.matches, invoice);

                                match.creditedAmount = receivedInvoice.creditedAmount;
                                match.paymentAmount = receivedInvoice.paymentAmount;

                                matchAdded = true;
                            }
                        }

                        if (correctInvoices.length !== result.length)
                        {
                            $parsingFailedDialogData.set({data, errorIndexes});
                        }
                    })
                    .catch(e => {
                        $parsingFailedDialogData.true();
                        console.error(e);
                    });
            } }/>
            </>
        ) : null }

        <InlineTable
            head={
                <TableHeader>
                    <TableCell size="small" />
                    <TableCell>Invoice</TableCell>
                    <TableCell align="right">Outstanding</TableCell>
                    <TableCell align="right">Amount Credited</TableCell>
                    <TableCell align="right">Payment Amount</TableCell>
                    <TableCell align="right">Overpaid/Shortpaid</TableCell>
                </TableHeader>
            }
        >
            {remittanceAdvice.matches.models.sort((a, b) => a.unfunded === b.unfunded ? 0 : a.unfunded ? 1 : -1).map(match =>
                <InvoiceRow 
                    key={match.cid} match={match} remittanceAdvice={remittanceAdvice} noEdits={noEdits}
                    $parsingFailedDialogData={ $parsingFailedDialogData } $openInvoiceLookup={$openInvoiceLookup}
                    missingSupplier={missingSupplier}
                />
            )}
            { !loading ? (
                <>
                    <TableTotals>
                        <TableCell align="left"/>
                        <TableCell>
                        { remittanceAdvice.invoices.length && !noEdits ?
                            <PickInvoice
                                invoices={ remittanceAdvice.invoices.filter(inv => inv.currency === remittanceAdvice.currency) }
                                matches={ remittanceAdvice.matches }
                                $parsingFailedDialogData={ $parsingFailedDialogData }
                            />
                            : null
                        }
                        </TableCell>
                        <TableCell align="right">
                            {asTotal(remittanceAdvice.matches.reduce((sum: number, match) => sum + match.outstandingAmount, 0), remittanceAdvice.currency)}
                        </TableCell>
                        <TableCell align="right">
                            {asTotal(remittanceAdvice.matches.reduce((sum: number, match) => sum + match.creditedAmount, 0), remittanceAdvice.currency)}
                        </TableCell>
                        <TableCell align="right">
                            {asTotal(remittanceAdvice.totalInvoiceAmount, remittanceAdvice.currency)}
                        </TableCell>
                    </TableTotals>
                </>
            ) : null }
        </InlineTable>

        {loading ?
            <Flex center stretch>
                <Loading />
            </Flex>
        : null}

        {$parsingFailedDialogData.value ?
            <PopupDialog
                title="Parsing issue"
                actions={
                    <>
                        <PrimaryButton onClick={ $parsingFailedDialogData.null }>Ok</PrimaryButton>
                    </>
                }
                >
                    {
                        $parsingFailedDialogData.value === true
                            ? <> We didn't find any new invoice information in the pasted content. </>
                            : <>
                                There's been an error parsing some of the pasted invoices:
                                <InlineTable children={ $parsingFailedDialogData.value.data.map((row, i) => (
                                    <TableRow style={ ($parsingFailedDialogData.value as FailedParsing).errorIndexes.indexOf(i) === -1 ? null : { backgroundColor: 'pink' } }>
                                        {
                                            Object.keys(row).map(rowKey => (
                                                <TableCell> { row[rowKey] } </TableCell>
                                            ))
                                        }
                                    </TableRow>
                                )) } />
                            </>
                    }
                    
            </PopupDialog>
        : null }

        { $openInvoiceLookup.value ? 
            <InvoiceLookupDialog $invoiceInfo={$openInvoiceLookup} remittanceAdvice={remittanceAdvice} />
            : undefined 
        }

    </CollapsableSection>
    );
}

function asTotal( x : number, currency : string ){
    return x ? asCurrency( x, currency ) : '';
}

export const PickInvoice = ({ invoices, matches, disabled, $parsingFailedDialogData } : {
    invoices : InvoiceInfo[]
    matches : Collection<InvoiceInfo>
    disabled? : boolean,
    $parsingFailedDialogData : Linked<FailedParsingType>
}) => {
    const $showDropdown = useLinked(false);

    return (
    <PickItem
        disabled={disabled}
        onChange={(newValue) => {
            if (newValue.invoiceNumber) {
                addMatch(matches, newValue);
            }
        }}
        $showDropdown={$showDropdown}
        filter={(x, text) => x.invoiceNumber.toLowerCase().indexOf(text.toLowerCase()) >= 0}
        onPaste={( evt => {
            const rows = getRowsForSingleColumnParsing(evt, $parsingFailedDialogData, $showDropdown);

            if (!rows) {
                return;
            }

            const errorIndexes: number[] = [];

            for (let i = 0; i < rows.length; i++) {
                const text = rows[i][0];
                const invoice = invoices.find(i => text.includes(i.invoiceNumber));

                if (invoice)
                {
                    if (!matches.includes(invoice)) {
                        addMatch(matches, invoice);
                    }
                }
                else {
                    errorIndexes.push(i);
                }
            }

            if (errorIndexes.length) {
                $parsingFailedDialogData.set({ data: rows.map(r => ({0: r})), errorIndexes} );
            }
        })}

        items={invoices.filter( inv => !matches.get( inv.id )) }
        placeholder="Enter/Paste Invoice #s"
    >
        {invoice => <PickInvoiceRow invoice={ invoice }/>}
    </PickItem>
    );
}


const PickInvoiceRow = styled(
    ({ invoice, className }) =>
        <div className={ className }>
            <div className="invoice">
                {invoice.invoiceNumber}
            </div>
            <div className="date">
                {asDate(invoice.dueDate)}
            </div>
            <div className="amount">
                {asCurrency(invoice.outstandingAmount, invoice.currency)}
            </div>
        </div>
)({
    display: "flex",
    flexDirection: "row",
    fontSize: 12,
    fontWeight: 400,
    margin: 4,

    "& .invoice" : {
        width : 180
    },

    "& .date" : {
        width : 120
    },

    "& .amount" : {
        fontWeight: 600
    }
})

export const InvoiceRow = ({ match, remittanceAdvice, noEdits, $parsingFailedDialogData, $openInvoiceLookup, missingSupplier }: {
    match: InvoiceInfo,
    remittanceAdvice: RemittanceAdvice | UnresolvedRemittanceAdvice,
    noEdits? : boolean,
    $parsingFailedDialogData: Linked<FailedParsingType>,
    $openInvoiceLookup : Linked<InvoiceInfo>,
    missingSupplier: boolean
}) => {
    const remove = () => {
        remittanceAdvice.matches = remittanceAdvice.matches.filter(inv => inv.id !== match.id) as any;
    };

    const iconClasses = inblockIconStyles();
    const missingSupplierTitle = 'Supplier is missing or multiple supplier are available in the RA.';

    return (
        <TableRow key={match.cid}>
            <TableCell>
                { !noEdits && !match.unfunded ? <DeleteIcon onClick={remove} /> : null }
                { match.id ? <InblockIcon src={'/assets/images/inblock_logo.png'} classes={iconClasses} /> : undefined }
                { match.unfunded ? <Chip label="Unfunded" color="primary" icon={ !match.id && !match.invSupplierName ? <WarningIcon titleAccess={missingSupplierTitle} /> : undefined } /> : undefined }
                { match.unfunded && !match.id && !noEdits ? <IconButton aria-label="Search" onClick={ () => $openInvoiceLookup.set(match) } style={{ visibility: "visible" }}><SearchIcon /></IconButton> : undefined }
            </TableCell>
            <TableCell>{match.invoiceNumber}</TableCell>
            <TableCell align="right">
                { match.unfunded ? 
                    <Input positive number $value={match.$.outstandingAmount} disabled={noEdits}
                        onPaste={ onPasteInInvoiceField(remittanceAdvice, match, 'outstandingAmount', true, $parsingFailedDialogData) } /> :
                    match.outstandingAmount
                }
            </TableCell>
            <TableCell align="right">
                <Input positive number $value={match.$.creditedAmount} disabled={noEdits}
                        aria-label="Credited Amount"
                       onPaste={ onPasteInInvoiceField(remittanceAdvice, match, 'creditedAmount', true, $parsingFailedDialogData) } />
            </TableCell>
            <TableCell align="right">
                <Input number $value={match.$.paymentAmount} disabled={noEdits} 
                        aria-label="Payment Amount"
                       onPaste={ onPasteInInvoiceField(remittanceAdvice, match, 'paymentAmount', true, $parsingFailedDialogData) } />
            </TableCell>
            <TableCell align="right">
                {asCurrency(match.getOverpayed(), match.currency)}
            </TableCell>
        </TableRow>
    );
};

const getRowsFromPaste = (evt: React.ClipboardEvent<HTMLInputElement>) => {
    const data = evt.clipboardData;
    const html = data.getData('text/html');

    if (!html) {
        return null;
    }

    const element = document.createElement('template');
    element.innerHTML = html;

    const table = element.content.querySelector('table');
    const tableRows = table.querySelectorAll('tr,th');

    const rows: NodeListOf<HTMLTableCellElement>[] = [];
    tableRows.forEach(r => rows.push(r.querySelectorAll('td')));

    return rows;
};

const getTextFromRows = (rows: NodeListOf<HTMLTableCellElement>[]) => {
    if (!rows) {
        return null;
    }

    const textRows: string[][] = [];

    rows.forEach(c => {
        const current = [];

        c.forEach(t => {
            current.push(t.innerText.trim());
        });

        textRows.push(current);
    });

    return textRows;
};

const getRowsForSingleColumnParsing = (evt: React.ClipboardEvent<HTMLInputElement>, $parsingFailedDialogData: Linked<FailedParsingType>, $showDropdown?: Linked<boolean>) => {
    const cellRows = getRowsFromPaste(evt);

    if (!cellRows) {
        return null;
    }

    evt.preventDefault();
    if ($showDropdown) {
        $showDropdown.false();
    }

    const rows = getTextFromRows(cellRows);

    if (!rows || !rows.every(r => r.length === 1)) {
        $parsingFailedDialogData.true();

        return null;
    }

    return rows;
}

const onPasteInInvoiceField = (
        remittanceAdvice: RemittanceAdvice | UnresolvedRemittanceAdvice, match: InvoiceInfo, fieldName: string,
        isNumber: boolean, $parsingFailedDialogData: Linked<FailedParsingType>
    ) => ( evt ) => {
        const rows = getRowsForSingleColumnParsing(evt, $parsingFailedDialogData);

        if (!rows) {
            return;
        }

        let currentIndex = remittanceAdvice.matches.indexOf(match);

        const count = remittanceAdvice.matches.length;

        for (const row of rows) {

            remittanceAdvice.matches.models[currentIndex][fieldName] = isNumber ? parseFloat(row[0]) || 0 : row[0];

            currentIndex++;

            if(currentIndex >= count) {
                break;
            }
        }
    };

const addMatch = (matches: Collection<InvoiceInfo>, newInvoice: InvoiceInfo) => {
    const copy = newInvoice.clone();
    copy.paymentAmount = copy.outstandingAmount;
    matches.push( copy );

    return copy;
};

export const InvoiceLookupDialog = ({ $invoiceInfo, remittanceAdvice} : {
    $invoiceInfo : Linked<InvoiceInfo>,
    remittanceAdvice : RemittanceAdvice
}) => {

    const invoice = $invoiceInfo.value;
    
    const companies = (Store.global as any).companies;
    const suppliers = (Store.global as any).suppliers;
    const $isOpen = toLinkedBoolean($invoiceInfo);
    const $supplier = useLinked<CompanyModel>(null);
    const $customer = useLinked<CompanyModel>(null);

    useEffect(() => {
        const supplier = companies.find(cmp => cmp.name === invoice.invSupplierName);
        const customer = companies.find(cmp => cmp.name === invoice.invCustomerName);
        if (supplier) {
            $supplier.set(supplier);
        }
        if (customer) {
            $customer.set(customer);
        }
    }, []);

    const $assets = useLinked<InvoiceAssetData[]>(null);
    const $isSearching = useLinked<boolean>(null);

    return (
        <PopupDialog
            $isOpen={ $isOpen }
            title="Search Invoice"
            maxWidth="lg"
            onSuccess={
                 () => Events.trigger( "selectedPayment:refresh" )
            }
            actions={
                <Button onClick={ $isOpen.false }>
                    Cancel
                </Button>
            }
        >
            <>
            <Columns>
                <Column>
                    <PickItem
                        $value={$supplier}
                        filter={(x, text) => {
                            return text.toLowerCase().split(/\s+/).every( y => x.name.toLowerCase().indexOf( y ) >= 0 );
                        }}
                        format={
                            (supplier : Supplier ) => supplier ? supplier.name : ''
                        }
                        items={suppliers}
                        placeholder={"Select supplier"}
                        showMore
                        insidePopup
                    >
                        { company => company.name } 
                    </PickItem>
                </Column>
                <Column>
                    <PickItem
                        $value={$customer}
                        filter={(x, text) => {
                            return text.toLowerCase().split(/\s+/).every( y => x.name.toLowerCase().indexOf( y ) >= 0 );
                        }}
                        format={
                            (company : CompanyModel ) => company ? company.name : ''
                        }
                        items={companies}
                        placeholder={"Select customer"}
                        showMore
                        insidePopup
                    >
                        { company => company.name } 
                    </PickItem>
                </Column>
            </Columns>

            <Flex row>
                <Flex grow />
                { 
                    $isSearching.value ? 
                        <Loading /> : 
                        <PrimaryButton onClick={async () => {
                            const supplierName = $supplier.value ? $supplier.value.name : '';
                            const customerName = $customer.value ? $customer.value.name : '';
                            $isSearching.true();
                            const invoices = await remittanceAdvice.invoiceLookup(invoice.invoiceNumber, supplierName, customerName);
                            invoice.invSupplierName = supplierName;
                            invoice.invCustomerName = customerName;
                            $invoiceInfo.set(invoice);
                            $assets.set(invoices);
                            $isSearching.false();
                        }}>
                            Search
                        </PrimaryButton>
                }
            </Flex>
            {$assets.value && $assets.value.length ?
                <>
                    <p>
                        { $assets.value.length } invoices found. Pick one.
                    </p>
                    <InlineTable>
                        <TableHeader>
                            <TableCell>Invoice Date</TableCell>
                            <TableCell>Invoice Due Date</TableCell>
                            <TableCell>Face Value</TableCell>
                            <TableCell></TableCell>
                        </TableHeader>
                        { $assets.value
                            .map(invAsset => 
                                {
                                    return <TableRow>
                                        <TableCell>
                                            { asTimelessDate(new Date(invAsset.invoiceDate)) }
                                        </TableCell>
                                        <TableCell>
                                            { asTimelessDate(new Date(invAsset.invoiceDueDate)) }
                                        </TableCell>
                                        <TableCell>
                                            { asCurrency(invAsset.faceValue, invAsset.currency) }
                                        </TableCell>
                                        <TableCell className="fit-width">
                                            <PrimaryButton
                                                onClick={() => {
                                                    invoice.id = invAsset.uid;
                                                    $invoiceInfo.set(invoice);
                                                    $isOpen.false();
                                                }}
                                            >
                                                Update Invoice RA
                                            </PrimaryButton>
                                        </TableCell>
                                    </TableRow>;
                                }
                            )
                        }

                    </InlineTable>
                </>
            : $isSearching.value === false ? <p>No invoices found.</p> : undefined }
            
            </>
        </PopupDialog>) 
}
